import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, DownloadCloud } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import {
  DropdownField,
  FormField,
  MoneyField,
  NumberField,
} from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  generateId,
  useApp,
  type CardGame,
  type LotStatus,
  type PurchaseLot,
} from "@/context/AppContext";
import { configForGame, GAME_PICKER_OPTIONS } from "@/features/tcgGameConfig";

const LOT_STATUSES: LotStatus[] = [
  "Unsorted",
  "Sorting",
  "Sorted",
  "Listed",
  "Partially Sold",
  "Closed",
];

const GAME_LABELS: Record<CardGame, string> = {
  Pokemon: "Pokémon",
  Magic: "Magic: The Gathering",
  "Yu-Gi-Oh": "Yu-Gi-Oh!",
  "One Piece": "One Piece",
  Other: "Other",
};

interface CreateLotSearch {
  seller?: string;
  purchaseLocation?: string;
  purchaseCost?: string;
  shippingCost?: string;
  taxFees?: string;
  purchaseDate?: string;
  ebayOrderId?: string;
  receiptNumber?: string;
  notes?: string;
}

function asStr(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute("/lots/create")({
  component: CreateLotPage,
  validateSearch: (raw): CreateLotSearch => ({
    seller: asStr(raw.seller),
    purchaseLocation: asStr(raw.purchaseLocation),
    purchaseCost: asStr(raw.purchaseCost),
    shippingCost: asStr(raw.shippingCost),
    taxFees: asStr(raw.taxFees),
    purchaseDate: asStr(raw.purchaseDate),
    ebayOrderId: asStr(raw.ebayOrderId),
    receiptNumber: asStr(raw.receiptNumber),
    notes: asStr(raw.notes),
  }),
});

function CreateLotPage() {
  const navigate = useNavigate();
  const { addLot, lots } = useApp();
  const params = Route.useSearch();

  const [game, setGame] = useState<CardGame>("Pokemon");
  const [lotType, setLotType] = useState<string>("Collection");
  const [seller, setSeller] = useState(params.seller ?? "");
  const [purchaseLocation, setPurchaseLocation] = useState(
    params.purchaseLocation ?? "",
  );
  const [purchaseDate, setPurchaseDate] = useState(
    params.purchaseDate ?? new Date().toISOString().split("T")[0],
  );
  const [purchaseCost, setPurchaseCost] = useState(params.purchaseCost ?? "");
  const [shippingCost, setShippingCost] = useState(params.shippingCost ?? "");
  const [taxFees, setTaxFees] = useState(params.taxFees ?? "");
  const [estimatedCards, setEstimatedCards] = useState("");
  const [notes, setNotes] = useState(
    params.notes ??
      (params.ebayOrderId ? `eBay Order: ${params.ebayOrderId}` : ""),
  );
  const [receiptNumber, setReceiptNumber] = useState(
    params.receiptNumber ?? params.ebayOrderId ?? "",
  );
  const [status, setStatus] = useState<LotStatus>("Unsorted");

  const [saving, setSaving] = useState(false);
  const [lotTypeError, setLotTypeError] = useState("");
  const [purchaseCostError, setPurchaseCostError] = useState("");
  const [purchaseDateError, setPurchaseDateError] = useState("");
  const [estimatedCardsError, setEstimatedCardsError] = useState("");

  const config = useMemo(() => configForGame(game), [game]);
  const lotTypeOptions = useMemo(
    () => config.lotTypes.map((t) => t.id),
    [config],
  );

  const handleGameSelect = (v: string) => {
    const g = v as CardGame;
    setGame(g);
    // Reset lot type to the new game's first option (the old game's types may
    // not exist for the new one).
    const next = configForGame(g).lotTypes[0];
    setLotType(next?.id ?? "Collection");
    setLotTypeError("");
    setEstimatedCards(
      next?.defaultCardCount ? String(next.defaultCardCount) : "",
    );
  };

  const handleLotTypeSelect = (v: string) => {
    setLotType(v);
    setLotTypeError("");
    const def = config.lotTypes.find((t) => t.id === v)?.defaultCardCount;
    if (def) setEstimatedCards(String(def));
  };

  const landedCost =
    (parseFloat(purchaseCost) || 0) +
    (parseFloat(shippingCost) || 0) +
    (parseFloat(taxFees) || 0);

  const costPerCard =
    (parseFloat(estimatedCards) || 0) > 0
      ? landedCost / parseFloat(estimatedCards)
      : 0;

  const receiptDuplicate = useMemo(() => {
    const normalized = receiptNumber.trim().toLowerCase();
    if (!normalized) return false;
    return lots.some(
      (l) =>
        l.receiptNumber && l.receiptNumber.trim().toLowerCase() === normalized,
    );
  }, [receiptNumber, lots]);

  const isValidDate = (s: string) =>
    /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(s));

  const handleSave = () => {
    let valid = true;
    if (!lotType) {
      setLotTypeError("Select a source type");
      valid = false;
    } else setLotTypeError("");

    if (
      !purchaseCost.trim() ||
      isNaN(parseFloat(purchaseCost)) ||
      parseFloat(purchaseCost) < 0
    ) {
      setPurchaseCostError(
        !purchaseCost.trim()
          ? "Enter a purchase cost"
          : "Enter a valid non-negative amount",
      );
      valid = false;
    } else setPurchaseCostError("");

    if (!isValidDate(purchaseDate)) {
      setPurchaseDateError("Enter a valid date (YYYY-MM-DD)");
      valid = false;
    } else setPurchaseDateError("");

    if (
      estimatedCards.trim() &&
      (isNaN(parseInt(estimatedCards)) || parseInt(estimatedCards) < 0)
    ) {
      setEstimatedCardsError("Enter a whole number of 0 or more");
      valid = false;
    } else setEstimatedCardsError("");

    if (!valid) return;

    setSaving(true);
    const lot: PurchaseLot = {
      id: generateId("LOT"),
      purchaseDate,
      seller,
      purchaseLocation: purchaseLocation.trim() || undefined,
      game,
      lotType,
      purchaseCost: parseFloat(purchaseCost) || 0,
      shippingCost: parseFloat(shippingCost) || 0,
      taxFees: parseFloat(taxFees) || 0,
      estimatedCards: parseInt(estimatedCards) || 0,
      notes,
      status,
      sortingBins: [],
      createdAt: new Date().toISOString(),
      ebayOrderId: params.ebayOrderId ?? undefined,
      receiptNumber: receiptNumber.trim() || undefined,
    };
    addLot(lot);
    setSaving(false);
    void navigate({ to: "/lots/$id", params: { id: lot.id }, replace: true });
  };

  return (
    <div className="space-y-3">
      <BackButton fallback={{ to: "/lots" }} />

      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-foreground">New Purchase Lot</h1>
        <button
          type="button"
          onClick={() => void navigate({ to: "/lots/ebay-import" })}
          className="inline-flex items-center gap-1.5 rounded-md border border-secondary/40 bg-secondary/20 px-3 py-2 text-xs font-semibold text-secondary-foreground hover:bg-secondary/30"
        >
          <DownloadCloud className="h-3.5 w-3.5" />
          Import from eBay
        </button>
      </div>

      {params.ebayOrderId && (
        <div className="flex items-center gap-2 rounded-md border border-secondary/40 bg-secondary/10 px-3 py-2 text-xs text-secondary-foreground">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span className="flex-1">
            Pre-filled from eBay order {params.ebayOrderId}
          </span>
        </div>
      )}

      <div className="rounded-md bg-primary p-4 text-primary-foreground">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium tracking-wide text-primary-foreground/70">
            Landed Cost
          </span>
          <span className="text-xl font-bold text-secondary">
            ${landedCost.toFixed(2)}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-xs font-medium tracking-wide text-primary-foreground/70">
            Cost Per Card
          </span>
          <span className="text-xl font-bold">${costPerCard.toFixed(4)}</span>
        </div>
      </div>

      <DropdownField
        label="TCG Game"
        value={game}
        options={GAME_PICKER_OPTIONS.map((g) => ({
          value: g,
          label: GAME_LABELS[g],
        }))}
        onChange={handleGameSelect}
        required
      />
      <DropdownField
        label="Source Type"
        value={lotType}
        options={lotTypeOptions}
        onChange={handleLotTypeSelect}
        required
        error={lotTypeError}
      />
      <FormField
        label="Seller"
        value={seller}
        onChangeText={setSeller}
        placeholder="Seller name or username"
      />
      <FormField
        label="Purchase Location"
        value={purchaseLocation}
        onChangeText={setPurchaseLocation}
        placeholder="eBay, TCGPlayer, local card shop, etc."
      />
      <div>
        <FormField
          label="Receipt / Order #"
          value={receiptNumber}
          onChangeText={setReceiptNumber}
          placeholder="e.g. TCG-8843, #001, RCPT-2025-01"
        />
        {receiptDuplicate && (
          <p className="-mt-3 mb-3 px-1 text-xs text-destructive">
            A lot with this receipt number already exists
          </p>
        )}
      </div>
      <FormField
        label="Purchase Date"
        value={purchaseDate}
        onChangeText={(v) => {
          setPurchaseDate(v);
          if (isValidDate(v)) setPurchaseDateError("");
        }}
        placeholder="YYYY-MM-DD"
        error={purchaseDateError}
      />
      <MoneyField
        label="Purchase Cost"
        value={purchaseCost}
        onChangeText={(v) => {
          setPurchaseCost(v);
          if (v.trim() && !isNaN(parseFloat(v)) && parseFloat(v) >= 0)
            setPurchaseCostError("");
        }}
        required
        error={purchaseCostError}
      />
      <MoneyField
        label="Shipping Cost"
        value={shippingCost}
        onChangeText={setShippingCost}
      />
      <MoneyField
        label="Tax / Fees"
        value={taxFees}
        onChangeText={setTaxFees}
      />
      <NumberField
        label="Estimated Card Count"
        value={estimatedCards}
        onChangeText={(v) => {
          setEstimatedCards(v);
          if (!v.trim() || (!isNaN(parseInt(v)) && parseInt(v) >= 0))
            setEstimatedCardsError("");
        }}
        placeholder="e.g. 5000"
        error={estimatedCardsError}
      />
      <DropdownField
        label="Initial Status"
        value={status}
        options={LOT_STATUSES}
        onChange={(v) => setStatus(v as LotStatus)}
      />
      <FormField
        label="Notes"
        value={notes}
        onChangeText={setNotes}
        placeholder="Any additional details..."
        multiline
      />

      <div className="pt-2">
        <PrimaryButton
          label="Create Lot"
          loading={saving}
          onClick={handleSave}
          className="w-full"
        />
      </div>
    </div>
  );
}
